import { useQuery, useMutation, useQueryClient, type UseMutationOptions, type UseQueryOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type { LmsProvisioningConfig, UpdateLmsProvisioningPayload } from './live-provisioning-types';

// ============================================
// Query keys
// ============================================

export const liveProvisioningKeys = {
  config: (serviceId: string) =>
    ['livestream', 'provisioning', serviceId] as const,
};

// ============================================
// Queries
// ============================================

export function useLmsProvisioningConfig(
  serviceId: string,
  options?: Omit<UseQueryOptions<LmsProvisioningConfig>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<LmsProvisioningConfig>({
    queryKey: liveProvisioningKeys.config(serviceId),
    enabled: !!serviceId,
    ...options,
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'streams')}/provisioning`);
      return res.data?.data ?? res.data;
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useUpdateLmsProvisioningConfig(
  serviceId: string,
  options?: UseMutationOptions<LmsProvisioningConfig, Error, UpdateLmsProvisioningPayload>,
) {
  const qc = useQueryClient();
  return useMutation<LmsProvisioningConfig, Error, UpdateLmsProvisioningPayload>({
    mutationFn: async (payload: UpdateLmsProvisioningPayload) => {
      const res = await serviceApiV3.put(`${mediaBase(serviceId, 'streams')}/provisioning`, payload);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: liveProvisioningKeys.config(serviceId) });
      options?.onSuccess?.(...args);
    },
  });
}
